/**
 * Central route registry for the Hummingbird platform.
 * Defines all valid route paths for better type safety and maintainability.
 */

export const ROUTES = {
    // Public
    LOGIN: '/login',
    SIGN_UP: '/sign-up',
    COMPLETE_ONBOARDING: '/complete-onboarding',
    /** Clerk org picker / sync workspace creation (authenticated shell, no app Layout). */
    SELECT_ORG: '/select-org',
    PUBLIC_SITE: '/public-site',
    PUBLIC_APPLY: '/public-site/apply/:listingId?',
    PUBLIC_PAY_RENT: '/public-site/pay-rent',
    PUBLIC_MAINTENANCE_REQUEST: '/public-site/maintenance-request',
    PUBLIC_SCHEDULE_TOUR: '/public-site/schedule-tour',
    PUBLIC_QUESTIONNAIRE: '/public-site/questionnaire/:leadId',
    ROOT: '/',

    // Legal (unauthenticated)
    LEGAL_PRIVACY: '/legal/privacy',
    LEGAL_TERMS: '/legal/terms',
    LEGAL_RETENTION: '/legal/retention',
    LEGAL_SUBPROCESSORS: '/legal/subprocessors',

    // App Core
    APP: '/app',
    APP_ACTIVITY: '/app/activity',
    UNAUTHORIZED: '/app/unauthorized',
    PM_CALENDAR: '/app/pm/calendar',
    PM_WORK_QUEUE: '/app/pm/work-queue',
    PM_EXCEPTIONS: '/app/pm/exceptions',
    COMMUNICATIONS: '/app/communications',
    NOTES: '/app/notes',
    SETTINGS: '/app/settings',
    INTEGRATIONS: '/app/settings/integrations',
    /** Org-scoped audit log (PM org admin + platform operators). */
    ADMIN_AUDIT: '/app/admin/audit',

    // Leasing
    LEASING: '/app/leasing',
    GUEST_CARDS: '/app/leasing/guest-cards',
    APPLICATIONS: '/app/leasing/applications',
    LEASES: '/app/leasing/leases',
    LEASING_RENEWALS: '/app/leasing/renewals',
    LEASING_ONBOARDING: '/app/leasing/onboarding',
    MARKETING: '/app/leasing/marketing',

    // Maintenance
    MAINTENANCE: '/app/maintenance',
    MAINTENANCE_COMPLETED: '/app/maintenance/completed',
    PROJECTS: '/app/maintenance/projects',
    PROJECT_DETAIL: '/app/maintenance/projects/:projectId',
    TURNS: '/app/maintenance/turns',
    INSPECTIONS: '/app/maintenance/inspections',
    INSPECTION_DETAIL: '/app/maintenance/inspections/:inspectionId',
    INSPECTION_SCHEDULES: '/app/maintenance/inspection-schedules',
    VENDORS: '/app/maintenance/vendors',
    VENDOR_DETAIL: '/app/maintenance/vendors/:vendorId',
    GAMIFICATION: '/app/maintenance/gamification',
    SOPS: '/app/maintenance/sops',

    // Accounting
    BILLS: '/app/accounting/bills',
    /** SMO / workspace-prefixed pilot (canonical target from `resolveCanonicalUrl`). */
    OPS_ACCOUNTING_BILLS: '/app/ops/accounting/bills',
    RECURRING_BILLS: '/app/accounting/recurring-bills',
    RECEIVABLES: '/app/accounting/receivables',
    ACCOUNTING_PAYMENT_PLANS: '/app/accounting/payment-plans',
    GL: '/app/accounting/gl',
    COA: '/app/accounting/coa',
    JOURNAL: '/app/accounting/journal',
    RECONCILIATION: '/app/accounting/reconciliation',
    ACCOUNTING_BANK_OPS: '/app/accounting/bank-operations',
    ACCOUNTING_PURCHASE_ORDERS: '/app/accounting/purchase-orders',
    ACCOUNTING_THREE_WAY_MATCH: '/app/accounting/three-way-match',
    CAM: '/app/accounting/cam',
    CLOSE: '/app/accounting/close',
    /** PM month management fee sweep (preview + idempotent commit). */
    ACCOUNTING_MANAGEMENT_FEE_SWEEP: '/app/accounting/management-fee-sweep',
    /** PM flight deck: preview + commit owner operating-cash distributions (per property, dry-run then seal). */
    ACCOUNTING_OWNER_DISTRIBUTION: '/app/accounting/owner-distributions',
    ACCOUNTING_DISTRIBUTIONS: '/app/accounting/distributions',
    DOCUMENTS_LIBRARY: '/app/documents/library',

    // Properties
    PROPERTIES: '/app/properties',
    PROPERTY_ONBOARD: '/app/properties/onboard',
    PROPERTY_OWNERS: '/app/properties/owners',
    PROPERTY_UNITS: '/app/properties/units',
    PROPERTY_PORTFOLIOS: '/app/properties/portfolios',
    PROPERTY_DETAIL: '/app/properties/:propertyId',

    // Tenants
    TENANTS: '/app/tenants',
    TENANT_DETAIL: '/app/tenants/:tenantId',
    COMMUNITY: '/app/community',

    // Owners (Back Office)
    OWNERS_MANAGEMENT: '/app/owners',
    OWNER_DETAIL: '/app/owners/:ownerId',
    /** PM / owner portal portfolio list (legacy string also matched in AppRouter). */
    OWNER_BACKOFFICE_PROPERTIES: '/app/owners/properties',
    OWNER_BACKOFFICE_PROPERTY_DETAIL: '/app/owners/properties/:propertyId',

    // Owner Portal (singular `/app/owner/...` = persona workspace; `/app/owners` = PM list + detail)
    OWNER_STATEMENTS: '/app/owner/statements',
    OWNER_PROPERTIES: '/app/owner/properties',
    OWNER_PROPERTY_DETAIL: '/app/owner/properties/:propertyId',
    OWNER_REPORTING: '/app/owner/reporting',
    OWNER_DASHBOARD: '/app/owner/dashboard',
    OWNER_COMMUNICATION: '/app/owner/communication',
    OWNER_DOCUMENTS: '/app/owner/documents',
    OWNER_SETTINGS: '/app/owner/settings',

    // Admin / Ops
    WORKFLOWS: '/app/workflows',
    REPORTING: '/app/reporting',
    REPORTING_SCHEDULED: '/app/reporting/scheduled',

    // Compliance
    COMPLIANCE: '/app/compliance',
    COMPLIANCE_INSPECTIONS: '/app/compliance/inspections',
    COMPLIANCE_HCV: '/app/compliance/hcv',
    COMPLIANCE_OBLIGATIONS: '/app/compliance/obligations',
    COMPLIANCE_GOVERNANCE: '/app/compliance/governance',

    // Tenant Portal
    MY_HOME: '/app/my-home',
    MY_PAYMENTS: '/app/my-payments',
    MY_MAINTENANCE: '/app/my-maintenance',
    MY_DOCUMENTS: '/app/my-documents',
    MY_COMMUNICATION: '/app/my-communication',
    MY_SETTINGS: '/app/my-settings',
    ONBOARDING: '/app/onboarding',
    LEASE_RENEWAL: '/app/lease-renewal',

    // Vendor Portal
    VENDOR_JOB_DETAIL: '/app/jobs/:jobId',
    VENDOR_JOBS: '/app/vendor/jobs',
    VENDOR_JOBS_ACTIVE: '/app/vendor/jobs/active',
    VENDOR_JOBS_COMPLETED: '/app/vendor/jobs/completed',
    VENDOR_JOBS_HISTORY: '/app/vendor/jobs/history',
    VENDOR_PROFILE: '/app/vendor/profile',
    VENDOR_PHOTOS: '/app/vendor/photos',
    VENDOR_CERTIFICATIONS: '/app/vendor/certifications',
    VENDOR_FINANCIALS_INVOICES: '/app/vendor/financials/invoices',
    VENDOR_FINANCIALS_PAYMENTS: '/app/vendor/financials/payments',
    VENDOR_FINANCIALS_EARNINGS: '/app/vendor/financials/earnings',
    VENDOR_FINANCIALS: '/app/vendor/financials',
    VENDOR_CALENDAR: '/app/vendor/calendar',
    VENDOR_PERFORMANCE: '/app/vendor/performance',
    VENDOR_PROFILE_BUSINESS: '/app/vendor/profile/business',
    VENDOR_PROFILE_REVIEWS: '/app/vendor/profile/reviews',
    VENDOR_MAP: '/app/map',
    VENDOR_COMMUNICATION: '/app/vendor/communication',
    VENDOR_SETTINGS: '/app/vendor/settings',

    // Investor Portal
    INVESTMENTS: '/app/investments',
    INVESTMENTS_PORTFOLIO: '/app/investments/portfolio',
    INVESTMENTS_DISTRIBUTIONS: '/app/investments/distributions',
    INVESTMENTS_ENTITIES: '/app/investments/entities',
    INVESTMENTS_RELATIONSHIPS: '/app/investments/relationships',
    INVESTOR_SETTINGS: '/app/investors/settings',
    INVESTMENT_DETAIL: '/app/investments/:id',
    INVESTOR_COMMUNICATION: '/app/investors/communication',
    INVESTOR_DOCUMENTS: '/app/investors/documents',
    INVESTOR_REPORTS: '/app/investors/reports',
    INVESTOR_PROPERTY_DETAIL: '/app/investors/properties/:propertyId',
} as const;

export type AppRoute = (typeof ROUTES)[keyof typeof ROUTES];
